/**
 * Area-Repository. Ausserhalb von `src/db/` greift niemand direkt auf Dexie zu.
 */
import type { Table } from 'dexie';

import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { AREA_COLORS, BEREICHS_VORSCHLAEGE } from '@/domain/areaColors';
import { kachelLayoutSpeichern } from '@/db/repositories/oberflaeche';
import { istStatistikId } from '@/domain/oberflaeche/statistik';
import { istZaehlerId } from '@/domain/oberflaeche/zaehler';
import { layoutIdBereich, leeresLayout } from '@/domain/oberflaeche/kacheln';
import { planFeste } from '@/domain/areas/feste';
import type { Area, ID } from '@/domain/types';

export type AreaDraft = Pick<Area, 'name' | 'color'> &
  Partial<Pick<Area, 'icon' | 'order' | 'lifeWheelEnabled' | 'beschreibung'>> & {
    /** Neuer Bereich startet mit leerer Seite und dem Kachel-Katalog (BE-06) statt mit dem Standard-Board. */
    leereSeite?: boolean;
  };

export async function listAreas(includeArchived = false): Promise<Area[]> {
  const all = await db.areas.orderBy('order').toArray();
  return includeArchived ? all : all.filter((a) => !a.archived);
}

export async function createArea(draft: AreaDraft): Promise<Area> {
  const ts = nowInstant();
  const maxOrder = (await db.areas.orderBy('order').last())?.order ?? -1;
  const area: Area = {
    id: newId(),
    name: draft.name.trim(),
    color: draft.color,
    icon: draft.icon,
    ...(draft.beschreibung ? { beschreibung: draft.beschreibung } : {}),
    order: draft.order ?? maxOrder + 1,
    archived: false,
    lifeWheelEnabled: draft.lifeWheelEnabled ?? true,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.areas.add(area);
  if (draft.leereSeite) await kachelLayoutSpeichern(leeresLayout(layoutIdBereich(area.id)));
  return area;
}

export async function updateArea(id: ID, patch: Partial<Omit<Area, 'id' | 'createdAt'>>): Promise<void> {
  await db.areas.update(id, { ...patch, updatedAt: nowInstant() });
}

/**
 * Archiviert statt zu loeschen: an einer Area haengen Termine, Aufgaben und
 * spaeter Ziele. Ein hartes Loeschen wuerde verwaiste `areaId`-Verweise
 * hinterlassen - deshalb gibt es nur `deleteArea` mit expliziter Entkopplung.
 */
export async function archiveArea(id: ID): Promise<void> {
  await updateArea(id, { archived: true });
}

export async function unarchiveArea(id: ID): Promise<void> {
  await updateArea(id, { archived: false });
}

/**
 * Loescht eine Area (30 Tage im Papierkorb, F-45) und haengt alles daran
 * Haengende auf `areaId: null` ("ohne Bereich") um. Laeuft in einer Transaktion, damit es
 * kein Zwischenergebnis mit kaputten Verweisen geben kann.
 *
 * Bewusst ueber ALLE Datentabellen statt einer Aufzaehlung: die fruehere
 * Aufzaehlung hatte Ziele, Ressourcen und Buecher vergessen, die danach auf
 * einen geloeschten Bereich zeigten. So ist auch jede kuenftige Tabelle mit
 * `areaId` automatisch abgedeckt. Ein voller Durchlauf kostet bei den
 * Datenmengen eines Menschen nichts, und Loeschen ist selten.
 */
export async function deleteArea(id: ID): Promise<void> {
  if ((await db.areas.get(id))?.fest) throw new Error('Feste Bereiche lassen sich nur archivieren.');
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'areas', zeileIds: [id] }, () => db.transaction('rw', db.dataTables, async () => {
    const ts = nowInstant();
    // Eigene Zaehler des Bereichs (BE-06) gehen mit.
    await db.oberflaeche.filter((r) => (istZaehlerId(r.id) || istStatistikId(r.id)) && (r as { areaId?: ID }).areaId === id).delete();
    for (const table of db.dataTables) {
      if (table === db.areas) continue;
      // Jede Tabelle hat ihren eigenen Zeilentyp; hier zaehlt nur `areaId`.
      await (table as unknown as Table<{ areaId?: ID | null; updatedAt?: string }>)
        .filter((row) => row.areaId === id)
        .modify({ areaId: null, updatedAt: ts });
    }
    // Wissensquellen haben mehrere Bereiche (V-13) - nur diesen herausnehmen.
    await db.books.where('areaIds').equals(id).modify((b) => {
      b.areaIds = b.areaIds.filter((a) => a !== id);
      b.updatedAt = ts;
    });
    // Das Kachel-Layout der Bereichsseite (BE-06) gehoert dazu und kommt mit zurueck, wenn der Bereich wiederhergestellt wird.
    await db.oberflaeche.delete(layoutIdBereich(id));
    await db.areas.delete(id);
  }));
}

/**
 * Sorgt dafuer, dass die drei festen Bereiche (BE-02) da und markiert sind -
 * bei jedem Start und nach dem Einspielen einer Sicherung, unabhaengig von der
 * Erststart-Markierung. Idempotent; wirft nie weg, was da ist.
 */
export async function festeBereicheSicherstellen(): Promise<number> {
  return db.transaction('rw', db.areas, async () => {
    const aktionen = planFeste(await db.areas.toArray());
    const ts = nowInstant();
    let order = ((await db.areas.orderBy('order').last())?.order ?? -1) + 1;
    for (const a of aktionen) {
      if (a.art === 'markieren') {
        const neueFarbe = a.farbe ? BEREICHS_VORSCHLAEGE.find((c) => c.key === a.farbe)?.value : undefined;
        await db.areas.update(a.id, { fest: a.fest, ...(a.name ? { name: a.name } : {}), ...(neueFarbe ? { color: neueFarbe } : {}), updatedAt: ts });
      } else {
        const v = a.vorgabe;
        const farbe = BEREICHS_VORSCHLAEGE.find((c) => c.key === v.farbe)?.value ?? AREA_COLORS[0]!.value;
        await db.areas.add({
          id: v.id, name: v.name, fest: v.fest, color: farbe, icon: v.icon, order: order++,
          archived: false, lifeWheelEnabled: true, createdAt: ts, updatedAt: ts,
        });
      }
    }
    return aktionen.length;
  });
}

/** Setzt die Sortierung neu - `ids` in der gewuenschten Reihenfolge. */
export async function reorderAreas(ids: ID[]): Promise<void> {
  const ts = nowInstant();
  await db.transaction('rw', db.areas, async () => {
    await Promise.all(ids.map((id, order) => db.areas.update(id, { order, updatedAt: ts })));
  });
}
